import { Spinner } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { noticeChipClassName } from "@/shared/ui/emptyState/public";
import { inlineSpinnerClassName } from "@/shared/ui/loadingState/public";
import { formatNumber } from "@/utils";
import {
  bandWidthRatio,
  curveRank,
  median,
  sortedCurve,
  type SortedCurve,
} from "./datasetOverview";
import type { ReferenceBandGrid } from "./diagnosticsSeries";
import { axisSample, percentileRank, type EpisodeDiagnosticAxis } from "./episodeDiagnosticValues";
import { bandWidthEmphasis, rankEmphasis, type OverviewEmphasis } from "./overviewEmphasis";
import type { EpisodeDiagnosticsState } from "./useEpisodeDiagnostics";
import styles from "./EpisodeOverviewCard.module.css";

/**
 * 채널 밴드 보기의 첫 칸에 표시하는 카드. 이 카드는 두 질문에 답한다:
 * - 이 데이터셋의 기준선은 얼마나 좁은가
 * - 이 에피소드는 그 기준선 안에서 어디에 있는가
 *
 * 채널 카드는 에피소드 하나를 채널·dim 으로 나눈다. 그래서 두 질문에 답하지 못한다. 이 카드는
 * 진단 설명과 나란히 한 칸을 쓴다. 그래서 사용자는 Action·Observation 채널 목록보다 먼저
 * 맥락을 함께 읽을 수 있다. 이 에피소드의 기준 밖 비율 숫자는 진단 표면 맨 위 요약 줄이
 * 표시한다. 그 숫자는 보기를 바꿔도 남아야 하는 머리 숫자이기 때문이다. 카드는 그 숫자가
 * 분포의 어디에 있는지를 곡선으로 표시한다.
 *
 * **기준 밖 지표는 서버 값만 쓴다.** 전체 에피소드의 분포는 진단 행
 * (`outside_reference_fraction`)에서만 나온다. 그래서 이 에피소드의 위치 표시도 같은 출처를
 * 써야 눈금이 맞는다. 채널 카드가 밴드 본문에서 다시 계산하는 값은 채널·dim 내역용이다.
 * 합계와 내역이 달라지지 않도록 여기서는 그 값을 쓰지 않는다.
 *
 * 데이터 특성 두 지표는 판정하지 않는다. 카드는 값과 데이터셋 안 위치를 표시하고, 분포 양끝만
 * 색으로 강조한다. 규칙은 `overviewEmphasis.ts` 가 소유한다.
 */

const AXES: readonly {
  key: EpisodeDiagnosticAxis;
  label: string;
  english: string;
}[] = [
  { key: "actionVariance", label: "행동 분산", english: "action variance" },
  { key: "stateNeighborCount", label: "상태 유사도", english: "state similarity" },
];

// 강조 수준을 값 텍스트 색으로 바꾼다. 붉은색은 판정이 아니라 먼저 확인할 곳이다(overviewEmphasis.ts).
const EMPHASIS_CLASS: Record<OverviewEmphasis, string> = {
  calm: styles.valueCalm,
  buffer: styles.valueBuffer,
  attention: styles.valueAttention,
};

export type EpisodeOverviewCardProps = {
  episodeUuid: string;
  episodeDiagnostics: EpisodeDiagnosticsState | null;
  // 기준 밴드. 밴드 폭 계산에 쓴다. null 은 미로드 또는 미산출을 뜻하며, 그때는 그 줄만 뺀다.
  band: ReferenceBandGrid | null;
};

export function EpisodeOverviewCard({
  episodeUuid,
  episodeDiagnostics,
  band,
}: EpisodeOverviewCardProps): ReactNode {
  const widthRatio = band === null ? null : bandWidthRatio(band);

  // 적재에 실패했거나 조회하지 않았으면 밴드 폭만 표시한다. 밴드 폭도 없으면 카드를 그리지 않는다.
  if (episodeDiagnostics === null || episodeDiagnostics.status !== "ready") {
    if (widthRatio === null) {
      return null;
    }
    return (
      <section className={styles.card} aria-label="Dataset 조망">
        <span className={styles.title}>이 에피소드</span>
        <BandWidthRow ratio={widthRatio} />
        {episodeDiagnostics?.status === "loading" ? (
          <span className={styles.loading} role="status">
            <Spinner size="sm" className={inlineSpinnerClassName} />
            {loadingCaption(episodeDiagnostics)}
          </span>
        ) : (
          // 실패는 다른 자리의 짧은 사유와 같은 주황 칩이다. 이 카드에는 다시 받는 경로가 없다.
          <span className={`${noticeChipClassName} ${styles.failure}`}>
            {loadingCaption(episodeDiagnostics)}
          </span>
        )}
      </section>
    );
  }

  const index = episodeDiagnostics.index;
  const values = index.get(episodeUuid) ?? null;
  const outside = values?.outsideReferenceFraction ?? null;
  const sample = axisSample(index, "outsideReferenceFraction");
  const curve = sortedCurve(sample);
  const sampleMedian = median(sample);
  const rank = curve !== null && outside !== null ? curveRank(curve, outside) : null;

  return (
    <section className={styles.card} aria-label="Dataset 조망">
      <span className={styles.title}>이 에피소드</span>

      {curve !== null ? (
        <>
          <SortedCurveChart curve={curve} rank={rank} />
          <div className={styles.axis}>
            <span>{formatPercent(curve.min)}</span>
            <span>{sampleMedian === null ? "" : `중앙값 ${formatPercent(sampleMedian)}`}</span>
            <span>{formatPercent(curve.max)}</span>
          </div>
          <div className={styles.axis}>
            <span>낮음</span>
            <span>
              {rank === null
                ? `${formatNumber(curve.values.length)}개 에피소드`
                : `${formatNumber(curve.values.length)}개 중 ${formatNumber(rank + 1)}번째`}
            </span>
            <span>높음</span>
          </div>
        </>
      ) : null}

      <div className={styles.rows}>
        {widthRatio !== null ? <BandWidthRow ratio={widthRatio} /> : null}
        {AXES.map((axis) => {
          const value = values?.[axis.key] ?? null;
          if (value === null) {
            return null;
          }
          const axisRank = percentileRank(axisSample(index, axis.key), value);
          const emphasis = rankEmphasis(axisRank);
          return (
            <div key={axis.key} className={styles.row}>
              <span className={styles.rowLabel}>
                {axis.label}
                <span className={styles.rowLabelEn}>({axis.english})</span>
              </span>
              <span
                className={
                  emphasis === null
                    ? styles.rowValue
                    : `${styles.rowValue} ${EMPHASIS_CLASS[emphasis]}`
                }
              >
                {formatAxisValue(value)}
              </span>
              <span className={styles.rowRank}>{formatRank(axisRank)}</span>
            </div>
          );
        })}
      </div>

      <span className={styles.caption}>
        곡선은 전체 {formatNumber(sample.length)}개 에피소드를 기준 밖 비율 오름차순으로 세운
        것입니다. 행동 분산·상태 유사도의 상위/하위는 이 Dataset 안에서만 비교되며, 높고 낮음이 곧
        좋고 나쁨은 아닙니다. 값의 붉은색은 잘못이 아니라 분포 양끝이라 먼저 확인해 볼 곳이라는
        뜻입니다.
      </span>
    </section>
  );
}

// 밴드 폭. 통로가 값 범위에서 차지하는 비율이다. 데이터 특성 지표와 달리 백분위가 없다.
// 데이터셋 하나에 값도 하나라서 비교할 표본이 없기 때문이다. 색도 백분위 대신 절대 임계로 구분한다.
function BandWidthRow({ ratio }: { ratio: number }): ReactNode {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>기준 밴드 폭</span>
      <span className={`${styles.rowValue} ${EMPHASIS_CLASS[bandWidthEmphasis(ratio)]}`}>
        {formatPercent(ratio)}
      </span>
      <span className={styles.rowRank}>값 범위 대비</span>
    </div>
  );
}

function loadingCaption(episodeDiagnostics: EpisodeDiagnosticsState | null): string {
  if (episodeDiagnostics?.status === "loading") {
    const progress = episodeDiagnostics.progress;
    return progress === null
      ? "에피소드 분포를 불러오는 중…"
      : `에피소드 분포를 불러오는 중 ${formatNumber(progress.loaded)} / ${formatNumber(progress.total)}`;
  }
  return "에피소드 분포를 불러오지 못했습니다.";
}

/**
 * 정렬 곡선. x 는 순위, y 는 값이다(arXiv 2605.01544 Fig.1 오른쪽과 같은 형식).
 *
 * 히스토그램을 쓰지 않는 이유는 다음과 같다. 이 지표는 성분 분위수와 회전 반경의 비교점
 * 비율이라서 값이 낮은 쪽에 몰린다. 값을 x 로 두면 오른쪽이 항상 비어 모양을 읽을 수 없다.
 * 순위를 x 로 두면 값이 치우쳐도 폭을 다 쓴다. 곡선이 완만한지, 끝에서 급하게 오르는지를
 * 보면 데이터셋이 고른지 알 수 있다.
 *
 * y 범위는 표본 최소~최대로 맞춘다. 0~100% 로 고정하면 곡선이 아래에 몰려 기울기를 읽을 수 없다.
 */
function SortedCurveChart({ curve, rank }: { curve: SortedCurve; rank: number | null }): ReactNode {
  const count = curve.values.length;
  const span = curve.max - curve.min;
  const xAt = (i: number): number => (count <= 1 ? 50 : (i / (count - 1)) * 100);
  // 값이 모두 같으면(span 0) 가운데에 수평선을 그린다. 0 으로 나누지 않는다.
  const yAt = (v: number): number => (span <= 0 ? 50 : 100 - ((v - curve.min) / span) * 100);
  const path = curve.values
    .map((value, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(2)} ${yAt(value).toFixed(2)}`)
    .join(" ");
  const markerX = rank === null ? null : xAt(Math.min(rank, count - 1));

  return (
    <svg
      className={styles.curve}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      role="img"
      aria-label={curveLabel(curve, rank)}
    >
      <path d={path} className={styles.curveLine} vectorEffect="non-scaling-stroke" />
      {markerX !== null ? (
        <line
          x1={markerX}
          x2={markerX}
          y1="0"
          y2="100"
          className={styles.curveMarker}
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}

function curveLabel(curve: SortedCurve, rank: number | null): string {
  const parts = [
    `전체 ${formatNumber(curve.values.length)}개 에피소드를 기준 밖 비율 오름차순으로 세운 곡선`,
    `${formatPercent(curve.min)}~${formatPercent(curve.max)}`,
  ];
  if (rank !== null) {
    parts.push(`이 에피소드 ${formatNumber(rank + 1)}번째`);
  }
  return parts.join(" · ");
}

function formatPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

// 무차원 값이므로 단위를 붙이지 않는다. 스케일이 지표마다 다르므로 유효숫자 3자리로 자른다.
function formatAxisValue(value: number): string {
  return Number(value.toPrecision(3)).toString();
}

/**
 * 분포에서의 위치를 가까운 쪽 끝 기준으로 적는다. 큰 쪽이면 "상위 N%", 작은 쪽이면 "하위 N%" 다.
 * 먼 쪽 기준으로 적으면(최소값을 "상위 100%") 읽는 사람이 위치를 거꾸로 이해한다.
 */
function formatRank(rank: number | null): string {
  if (rank === null) {
    return "—";
  }
  if (rank >= 0.5) {
    return `상위 ${Math.max(1, Math.round((1 - rank) * 100))}%`;
  }
  return `하위 ${Math.max(1, Math.round(rank * 100))}%`;
}
